import { ENTITY } from 'modules/campaigns/config/routes';
import addRecommendationsQueryParams from '../../../modules/recommendations/utils/queryParamUtils';
import patchOperation from '../patchOperation';

export const ENTITY_TO_API_PATH = {
    [ENTITY.CAMPAIGN]: 'campaigns',
    [ENTITY.PMP_DEAL]: 'pmp-deals',
    [ENTITY.CAMPAIGN_VIDEO]: 'campaign-video',
    [ENTITY.VIDEO_PMP_DEAL]: 'video-pmp-deal',
};

export const SIMULATOR_METADATA_ENDPOINT = '/performance-simulator/metadata';

export const campaignsAPIPrefix = ({
    accountId,
    campaignId,
    entityType = ENTITY.CAMPAIGN,
    suffix = '',
    includeCreativeLibraryCampaigns,
}) => {
    const apiPath = ENTITY_TO_API_PATH[entityType] || ENTITY_TO_API_PATH[ENTITY.CAMPAIGN];
    const includeCreativeLibraryIndication = includeCreativeLibraryCampaigns
        ? `?include_creative_library_campaigns=${includeCreativeLibraryCampaigns}`
        : '';

    return `/${accountId}/${apiPath}${suffix}${campaignId ? `/${campaignId}` : ''}${includeCreativeLibraryIndication}`;
};

export const campaignsCreateAPIPrefix = ({ entityType, ...rest }) => {
    if (entityType === ENTITY.RTB_OPEN_EXCHANGE) {
        return campaignsAPIPrefix({ entityType, ...rest, suffix: '/open-exchange' });
    }
    return campaignsAPIPrefix({ entityType, ...rest });
};

export const campaignsApiFactory = ({ callGetApi, callPostApi, callPatchApi, callDeleteApi, callPutApi }) => ({
    getCampaigns: ({
        accountId,
        page,
        pageSize,
        searchText,
        sort,
        dimension,
        status,
        apiCacheKey,
        allowMultiRequestCache,
        campaignIds,
        campaignGroupId,
        includeCreativeLibraryCampaigns,
    }) =>
        callGetApi(campaignsAPIPrefix({ accountId, includeCreativeLibraryCampaigns }), {
            paginationEnabled: true,
            page,
            pageSize,
            searchText,
            sort,
            dimension,
            status,
            singleRequestCacheKey: apiCacheKey,
            allowMultiRequestCache,
            campaignIds,
            campaignGroupId,
        }),
    getCampaign: ({ accountId, campaignId, entityType, query }) =>
        callGetApi(campaignsAPIPrefix({ accountId, campaignId, entityType }), query),
    createCampaign: ({ accountId, campaign, entityType, recommendationType, recommendationId }) =>
        callPostApi(
            addRecommendationsQueryParams(campaignsCreateAPIPrefix({ accountId, entityType }), {
                recommendationType,
                recommendationId,
            }),
            campaign
        ),
    updateCampaign: ({ accountId, campaignId, changes, recommendationType, recommendationId, entityType }) =>
        callPutApi(
            addRecommendationsQueryParams(campaignsAPIPrefix({ accountId, campaignId, entityType }), {
                recommendationType,
                recommendationId,
            }),
            changes
        ),
    updateCampaigns: (accountId, changes) => callPutApi(`${campaignsAPIPrefix({ accountId })}/bulk-update`, changes),
    duplicateCampaign: ({
        accountId,
        campaignId,
        changes,
        destinationAccount,
        recommendationType,
        recommendationId,
        entityType,
    }) =>
        callPostApi(
            addRecommendationsQueryParams(campaignsAPIPrefix({ accountId, campaignId, entityType }), {
                recommendationType,
                recommendationId,
                destinationAccount,
            }),
            changes
        ),
    deleteCampaign: ({ accountId, campaignId, entityType }) =>
        callDeleteApi(campaignsAPIPrefix({ accountId, campaignId, entityType })),
    addToCampaignProperty: ({ accountId, campaignId, changes, entityType }) =>
        callPatchApi(campaignsAPIPrefix({ accountId, campaignId, entityType }), changes, patchOperation.ADD),
    removeFromCampaignProperty: ({ accountId, campaignId, changes, entityType }) =>
        callPatchApi(campaignsAPIPrefix({ accountId, campaignId, entityType }), changes, patchOperation.REMOVE),
    replaceCampaignProperty: ({ accountId, campaignId, changes, entityType }) =>
        callPatchApi(campaignsAPIPrefix({ accountId, campaignId, entityType }), changes, patchOperation.REPLACE),
    getDefaultRecommendedCta: (accountId, campaignId) =>
        callGetApi(`${campaignsAPIPrefix({ accountId, campaignId })}/cta/default`),
    getSimulatorMetadata: (accountId, campaignId) => {
        return callGetApi(`${campaignsAPIPrefix({ accountId, campaignId })}${SIMULATOR_METADATA_ENDPOINT}`);
    },
});
